import React, { useEffect, useRef, useState } from "react";
import { Table, TableColumnsType, Spin, Menu, Dropdown } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../store";
import { getProductPartsAPI } from "../productApis";
import { getVariableLabel } from "../../StockProcessManager/constants";
import { DeleteStockPartAPI } from "../../../Redux/stockProcessManager";
import { getDecryptedName } from "../../../../helpers/bycrypt";
import { formatToLocalDate, useDebounce } from "../../../../helpers/utils";
import NoDataMessage from "../../../Components/NoData";
import { svg } from "../../../../Assets/svg";
import InfiniteScroll from "react-infinite-scroll-component";
import CommonDeleteModal from "../../../Components/Modals/CommonDeleteModal";

interface ProductPartsProps {
  productId?: string;
}

const ProductParts: React.FC<ProductPartsProps> = ({ productId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [partsList, setPartsList] = useState<any[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [isDeleteId, setIsDeleteId] = useState<any>(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const searchBarHandler = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(e.target.value);
  const searchBarClean = () => setSearch("");

  const fetchParts = async () => {
    if (!productId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    const filters = {
      product_id: productId,
      search: debouncedSearch,
    };

    dispatch(
      getProductPartsAPI({
        filters,
        signal: controller.signal,
        onSuccess: (data: any) => {
          setPartsList(data?.parts_data || []);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (productId) {
      fetchParts();
    }
  }, [productId, debouncedSearch]);

  const handleDeletePartData = () => {
    const payload = { id: isDeleteId };
    setDeleteLoading(true);
    const onDeleteSuceess = () => {
      setDeleteLoading(false);
      setIsDeleteModal(false);
      setIsDeleteId(null);
      fetchParts();
    };
    dispatch(DeleteStockPartAPI(payload, onDeleteSuceess));
  };

  const MoreOptionsMenu = (record: any) => {
    return (
      <Menu>
        <Menu.Item
          key="1"
          onClick={() => {
            navigate(`/process-manager/part/create/${record?._id}`, {
              state: { returnUrl: location.pathname + location.search },
            });
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Modify
          </div>
        </Menu.Item>
        <Menu.Item
          key="2"
          onClick={() => {
            navigate(`/process-manager/part/duplicate/${record?._id}`, {
              state: { returnUrl: location.pathname + location.search },
            });
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            Duplicate
          </div>
        </Menu.Item>
        <Menu.Item
          key="3"
          onClick={() => {
            setIsDeleteId(record?._id);
            setIsDeleteModal(true);
          }}
        >
          <div className="text-danger d-flex align-items-center gap-2 svg-20">
            Remove
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const renderProcessesSummary = (record: any) => {
    const standardCount = record?.part_processes?.length || 0;
    const isAssembly = !!record?.is_factory_assembly;
    const assemblyCount = isAssembly
      ? record?.part_assembly_processes?.length || 0
      : record?.part_assembly_processes?.length || 0;

    if (standardCount > 0 && assemblyCount > 0) {
      return (
        <div className="d-flex flex-column gap-1">
          <div>Standard: {standardCount}</div>
          <div>Assembly: {assemblyCount}</div>
        </div>
      );
    }
    if (standardCount > 0) {
      return `Standard: ${standardCount}`;
    }
    if (assemblyCount > 0) {
      return `Assembly: ${assemblyCount}`;
    }
    return "-";
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Part Name",
      dataIndex: "name",
      key: "name",
      render: (name: any) => name || "-",
    },
    {
      title: "Stock Type",
      dataIndex: "stock_id",
      key: "stock_id",
      render: (stock: any) =>
        stock?.name || stock?.stock_type_name || stock?.title || "-",
    },
    {
      title: "Processes",
      dataIndex: "part_processes",
      key: "part_processes",
      render: (_: any, record: any) => renderProcessesSummary(record),
    },
    {
      title: "Components",
      dataIndex: "component_ids",
      key: "component_ids",
      render: (component_ids: any[]) =>
        component_ids?.length ? `${component_ids.length} Components` : "0",
    },
    {
      title: "Variables",
      dataIndex: "bom_variables",
      key: "bom_variables",
      render: (bom_variables: any[]) => {
        const requiredVars = bom_variables
          ?.filter((v: any) => v.is_required)
          .map((v: any) =>
            getVariableLabel ? getVariableLabel(v.key) : v.key,
          );
        return requiredVars?.length ? requiredVars.join(", ") : "-";
      },
    },
    {
      title: "Updated By",
      dataIndex: "updated_by",
      key: "updated_by",
      render: (cb: any, record: any) => {
        const user = cb || record?.created_by;
        const fullName = getDecryptedName(user) || "-";
        return <span>{fullName}</span>;
      },
    },
    {
      title: "Updated On",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (date: string) => formatToLocalDate(date, true) || "-",
    },
    {
      title: "Action",
      key: "action",
      align: "center" as const,
      render: (action: any, record: any) => (
        <div
          className="d-flex justify-content-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            overlay={() => MoreOptionsMenu(record)}
            trigger={["click"]}
            placement="bottomRight"
            className="cursor-pointer"
            overlayClassName="moreOptionDropdown"
          >
            <span className="svg-28 svg-outline-primary">
              {svg?.tableMoreOption}
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 d-flex justify-content-between align-items-center flex-shrink-0">
        <h3 className="fw-bold mb-0 d-flex gap-2 align-items-center">
          Parts {partsList.length > 0 && <span>({partsList.length})</span>}
        </h3>
        <div
          className={`d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-end align-items-center ms-auto order-sm-2 order-3`}
        >
          <div
            className={`pageSearch mw-100 position-relative d-inline-block mt-sm-0 mt-2`}
          >
            <input
              type="text"
              value={search}
              className="form-control form-control-lg"
              onChange={searchBarHandler}
              placeholder="Search"
            />
            {search?.length ? (
              <span className="cursor-pointer" onClick={searchBarClean}>
                {svg?.searchClear}
              </span>
            ) : (
              <span>{svg?.search}</span>
            )}
          </div>
        </div>
      </div>

      <div className="card page-table-card variantsTableCard border-0 shadow rounded-4">
        <div
          className="card-body overflow-y-auto h-100 p-0"
          id="partsScrollableDiv"
        >
          {loading ? (
            <div className="text-center p-5">
              <Spin size="large" />
            </div>
          ) : partsList.length === 0 ? (
            <div className="p-4">
              <NoDataMessage
                icon={svg?.noProductsIcon}
                text="No parts found for this product."
              />
            </div>
          ) : (
            <div style={{ height: "100%" }}>
              <div className="table-responsive">
                <Table
                  dataSource={partsList}
                  pagination={false}
                  columns={columns}
                  rowKey={(record) => record._id}
                  className="variant-edit-table bg-white"
                  bordered
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {isDeleteModal && (
        <CommonDeleteModal
          visible={isDeleteModal}
          setIsModalVisible={setIsDeleteModal}
          handleDelete={handleDeletePartData}
          title={"Remove Part, Are You Sure?"}
          description={
            "Are you sure you want to remove this part? All associated data will be permanently deleted."
          }
          setIsDeleteId={setIsDeleteId}
          isLoading={deleteLoading}
          buttonName="REMOVE"
        />
      )}
    </>
  );
};

export default ProductParts;
